import React, { useState, useEffect, useRef } from 'react';
import { FormInput, Textarea, Button } from './ui';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './api';
import { safeSetLocal } from './runtimeConfig';
import './Lobby.css';
import * as constants from './constants';
import { usePublicGames } from './hooks/usePublicGames';
import { useLeaderboard } from './hooks/useLeaderboard';
interface LobbyProps {
  user: {
    name?: string;
    email?: string;
    picture?: string;
  };
  onLogout: () => void;
}

const Lobby: React.FC<LobbyProps> = ({ user, onLogout }) => {
  const [error, setError] = useState<string | null>(null);
  const { games, error: gamesError } = usePublicGames(5000);
  const leaderboard = useLeaderboard(10000);
  const [creatorStats, setCreatorStats] = useState<Record<string, { wins: number; resignations: number }>>({});
  const [joinCode, setJoinCode] = useState('');
  const [gameName, setGameName] = useState('');
  const [gameDescription, setGameDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const JOIN_CODE_LENGTH = 8;
  const navigate = useNavigate();
  const actingRef = useRef(false);

  useEffect(() => {
    // surface errors from the games hook
    if (gamesError) setError('Could not load games. Please try again later.');
  }, [gamesError]);

  useEffect(() => {
    // When public games update, fetch any missing creator stats
    const fetchMissingCreatorStats = async () => {
      try {
        const emails = new Set<string>();
        for (const g of games || []) {
          const creator = g.players?.[0]?.player_email;
          if (creator) emails.add(creator);
        }
        const missing = Array.from(emails).filter(e => !(e in creatorStats));
        if (!missing.length) return;
        const entries: [string, { wins: number; resignations: number }][] = [];
        await Promise.all(missing.map(async (e) => {
          try {
            const res = await apiFetch(`${constants.API_PLAYER_STATS}?email=${encodeURIComponent(e)}`);
            if (res.status === 401) { window.location.href = '/'; return; }
            if (res.ok) {
              const s = await res.json();
              entries.push([e, { wins: s.Wins ?? s.wins ?? 0, resignations: s.Resignations ?? s.resignations ?? 0 }]);
            }
          } catch {}
        }));
        if (entries.length) {
          setCreatorStats(prev => ({ ...prev, ...Object.fromEntries(entries) }));
        }
      } catch (err) {
        setError('Could not load games. Please try again later.');
        console.error(err);
      }
    };

    fetchMissingCreatorStats();
  }, [games, creatorStats]);

  const createGame = async () => {
    if (!gameName.trim()) {
      alert('Please enter a name for the game.');
      return;
    }
    try {
      if (actingRef.current || submitting) return;
      actingRef.current = true;
      setSubmitting(true);
      const response = await apiFetch(constants.API_GAMES, {
        method: 'POST',
        headers: {
          [constants.HEADER_CONTENT_TYPE]: constants.CONTENT_TYPE_JSON,
        },
        body: JSON.stringify({
          name: gameName,
          description: gameDescription,
          private: isPrivate,
          player_name: user.name,
          player_email: user.email || '',
        }),
      });
      if (response.status === 401) { window.location.href = '/'; return; }
      if (!response.ok) {
        throw new Error('Failed to create game');
      }
      const newGameInfo = await response.json();
      try { if (user?.email) safeSetLocal('player_email', user.email); } catch {}
      try { if (user?.email) safeSetLocal('player_email', user.email); } catch {}
      safeSetLocal('game_code', newGameInfo.join_code);
      navigate(`/game/${newGameInfo.join_code}`);
    } catch (err) {
      alert('Error creating game. Please try again.');
      console.error(err);
    } finally { setSubmitting(false); actingRef.current = false; }
  };

  const joinGame = async (code?: string) => {
    const gameCodeToUse = code || joinCode;
    if (!gameCodeToUse.trim()) {
      alert('Please enter a game code.');
      return;
    }
    try {
      if (actingRef.current || submitting) return;
      actingRef.current = true;
      setSubmitting(true);
      const response = await apiFetch(constants.API_GAMES_JOIN, {
        method: 'POST',
        headers: {
          [constants.HEADER_CONTENT_TYPE]: constants.CONTENT_TYPE_JSON,
        },
        body: JSON.stringify({ 
          join_code: gameCodeToUse,
          player_name: user.name,
          player_email: user.email || '',
         }),
      });

      if (response.status === 401) { window.location.href = '/'; return; }
      if (response.ok) {
        const joinedGameInfo = await response.json();
        try { if (user?.email) safeSetLocal('player_email', user.email); } catch {}
        safeSetLocal('game_code', joinedGameInfo.join_code);
        navigate(`/game/${joinedGameInfo.join_code}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to join game');
      }
    } catch (err: any) {
      alert(`Error joining game: ${err.message}`);
      console.error(err);
    } finally { setSubmitting(false); actingRef.current = false; }
  };

  return (
    <div>
      <main className="page-main">
        <h2>Game Lobby</h2>
        <div className="lobby-top">
              {/* Create Game Section */}
              <div>
                <h4>Create New Game</h4>
                <FormInput
                  type="text"
                  placeholder="Game Name"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  maxLength={32}
                />
                <Textarea
                  placeholder="Description"
                  value={gameDescription}
                  onChange={(e) => setGameDescription(e.target.value)}
                  maxLength={256}
                />
            <div className="mb-20">
              <input 
                type="checkbox" 
                id="privateGame" 
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
              <label htmlFor="privateGame" className="ml-10">Private Game</label>
            </div>
            <Button onClick={createGame} disabled={submitting || gameName.trim().length < 5} className="full-width">
              Create Game
            </Button>
          </div>

          {/* Join Game Section */}
          <div>
            <h4>Join Existing Game</h4>
            <div className="join-row">
              <FormInput
                type="text"
                placeholder="Enter game code"
                value={joinCode}
                onChange={(e) => {
                  const raw = (e.target as HTMLInputElement).value.toUpperCase();
                  const sanitized = raw.replace(/[^A-Z0-9]/g, '').slice(0, JOIN_CODE_LENGTH);
                  setJoinCode(sanitized);
                }}
                maxLength={JOIN_CODE_LENGTH}
                className="flex-grow-1"
              />
              <Button onClick={() => joinGame()} disabled={submitting || joinCode.trim().length !== JOIN_CODE_LENGTH}>
                Join Game
              </Button>
            </div>
          </div>
        </div>

        <h4 className="mt-20">Available Public Games</h4>
        {error && <p className="error-message">{error}</p>}
        <div className="table-wrap">
          <div className="game-list game-list-header">
            <div>Game</div>
            <div>Description</div>
            <div>Players</div>
            <div>Code</div>
            <div>Victory</div>
            <div>Resignations</div>
          </div>
          {games.length > 0 ? (
            games.map(game => (
              <div key={game.ID} className="game-list">
                <div className="game-name">
                  <Button variant="link" onClick={() => joinGame(game.join_code)} className="link-button" disabled={submitting}>
                    {game.name}
                  </Button>
                </div>
                <div className="game-description">{game.description}</div>
                <div className="game-players">{game.players?.length || 0}/2</div>
                <div className="game-waiting">{game.join_code || '-'}</div>
                <div className="game-wins">{creatorStats[game.players?.[0]?.player_email || '']?.wins ?? 0}</div>
                <div className="game-resignations">{creatorStats[game.players?.[0]?.player_email || '']?.resignations ?? 0}</div>
              </div>
            ))
          ) : (
            <p>No public games available. Create one!</p>
          )}
        </div>
      </main>
      <section className="section-padding">
        <h4 className="mt-20">Top 10 Players</h4>
        <div className="table-wrap">
          <div className="rank-list rank-list-header">
            <div>#</div>
            <div>Name</div>
            <div>Wins</div>
            <div>Defeats</div>
            <div>Resignations</div>
          </div>
          {leaderboard.length > 0 ? (
            leaderboard.map((p, idx) => {
              const gp = (p as any).GamesPlayed ?? (p as any).games_played ?? 0;
              const wins = (p as any).Wins ?? (p as any).wins ?? 0;
              const resign = (p as any).Resignations ?? (p as any).resignations ?? 0;
              const defeatsRow = Math.max(0, gp - wins - resign);
              const name = (p as any).PlayerName ?? (p as any).player_name ?? 'Unknown';
              return (
                <div key={(p as any).ID ?? idx} className="rank-list">
                  <div>{idx + 1}</div>
                  <div>{name}</div>
                  <div>{wins}</div>
                  <div>{defeatsRow}</div>
                  <div>{resign}</div>
                </div>
              );
            })
          ) : (
            <p>No players yet. Play some games!</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Lobby;
